// src/pages/Breeding.js
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import QuienesSomos from './quienes_somos';
import HomeProyectos from './home_proyectos';
import BotonWhatsapp from '../components/boton_whatsapp';
import Contacto from '../components/contacto';
import Footer from '../components/footer';
import Menu from '../components/menu';

const Breeding = ({ onChangeLanguage, onNavigateToProjects }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [temaSeleccionado, setTemaSeleccionado] = useState('tema1');
  const [scrollY, setScrollY] = useState(0);

  // Configuración de temas con clases de Tailwind
  const temas = {
    tema1: {
      bg: 'bg-white',
      text: 'text-gray-800',
      card: 'bg-blue-50',
      cardHover: 'hover:bg-blue-100',
      border: 'border-blue-200',
      gradient: 'from-blue-500 to-blue-700',
      accent: 'text-blue-600',
      accentBg: 'bg-blue-600',
      accentHover: 'hover:bg-blue-700',
      selectBg: 'bg-blue-50',
      selectBorder: 'border-blue-300',
      heroGradient: 'from-blue-600 to-blue-900',
      badge: 'bg-blue-100 text-blue-800',
    },
    tema2: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-900',
      card: 'bg-emerald-100',
      cardHover: 'hover:bg-emerald-200',
      border: 'border-emerald-200',
      gradient: 'from-emerald-500 to-emerald-700',
      accent: 'text-emerald-600',
      accentBg: 'bg-emerald-600',
      accentHover: 'hover:bg-emerald-700',
      selectBg: 'bg-emerald-50',
      selectBorder: 'border-emerald-300',
      heroGradient: 'from-emerald-600 to-emerald-900',
      badge: 'bg-emerald-100 text-emerald-800',
    },
    tema3: {
      bg: 'bg-red-50',
      text: 'text-red-900',
      card: 'bg-red-100',
      cardHover: 'hover:bg-red-200',
      border: 'border-red-200',
      gradient: 'from-red-500 to-red-700',
      accent: 'text-red-600',
      accentBg: 'bg-red-600',
      accentHover: 'hover:bg-red-700',
      selectBg: 'bg-red-50',
      selectBorder: 'border-red-300',
      heroGradient: 'from-red-600 to-red-900',
      badge: 'bg-red-100 text-red-800',
    },
    tema4: {
      bg: 'bg-purple-50',
      text: 'text-purple-900',
      card: 'bg-purple-100',
      cardHover: 'hover:bg-purple-200',
      border: 'border-purple-200',
      gradient: 'from-purple-500 to-purple-700',
      accent: 'text-purple-600',
      accentBg: 'bg-purple-600',
      accentHover: 'hover:bg-purple-700',
      selectBg: 'bg-purple-50',
      selectBorder: 'border-purple-300',
      heroGradient: 'from-purple-600 to-purple-900',
      badge: 'bg-purple-100 text-purple-800',
    }
  };

  const temaActual = temas[temaSeleccionado];

  // Función de scroll suave
  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    const sectionId = location.state?.scrollToSection;
    if (!sectionId) return undefined;

    const timeout = setTimeout(() => scrollToSection(sectionId), 150);
    return () => clearTimeout(timeout);
  }, [location.key, location.state, scrollToSection]);

  // Efecto parallax
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`min-h-screen ${temaActual.bg} ${temaActual.text}`}>
      
      <Menu
        temaActual={temaActual}
        onChangeLanguage={onChangeLanguage}
        onNavigateToProjects={onNavigateToProjects}
        onNavigateToSection={scrollToSection}
      />

      {/* HERO CON PARALLAX */}
      <div id="inicio" className={`relative h-screen overflow-hidden pt-16 bg-gradient-to-r ${temaActual.heroGradient}`}>
        <motion.div 
          className="absolute inset-0"
          style={{
            y: scrollY * 0.5,
          }}
        >
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: 'url(/images/portada.jpg)',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          </div>
        </motion.div>
        
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              {t('hero.titleLine1')}
              <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                {t('hero.titleLine2')}
              </span>
            </h1>
            <p className="text-xl mb-8 text-gray-200">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={onNavigateToProjects}
                className="rounded-lg border-2 border-white bg-white/20 px-8 py-4 font-semibold backdrop-blur-sm transition-all hover:bg-white/30"
              >
                {t('hero.seeProjects')}
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center animate-bounce">
            <div className="w-1 h-3 bg-white rounded-full mt-2" />
          </div>
        </div>
      </div>

      <HomeProyectos temaActual={temaActual} />

      <QuienesSomos />
      <Contacto temaActual={temaActual} />
      <Footer
        onNavigateToProjects={onNavigateToProjects}
        onNavigateToSection={scrollToSection}
      />
      <BotonWhatsapp />
    </div>
  );
};

export default Breeding;